import { createPortal } from 'react-dom';
import {
  EditOutlined,
  DeleteOutlined,
  UserAddOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { ClickAwayListener, Tooltip } from '@mui/material';
import { Popover, notification } from 'antd';
import { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { PRIORITY_COLORS, PRIORITY_ICON, PRIORITY_LABEL } from '../../../../../js/constants.js';
import { tasksApi } from '../../../../../services/tasksApi.js';
import UserAvatar from '../../../atoms/ticket-drawer/UserAvatar.jsx';
import AssigneePill from './AssigneePill.jsx';
import CreatorPill from './CreatorPill.jsx';
import CustomFieldPill from './CustomFieldPill.jsx';
import DueDatePill from './DueDatePill.jsx';
import UserIcon from './Icons/UserIcon.jsx';
import PriorityPill from './PriorityPill.jsx';
import ProjectPill from './ProjectPill.jsx';
import ResolutionPill from './ResolutionPill.jsx';
import TagPill from './TagPill.jsx';

import '../../../../../assets/style/taskFilter.css';
import './style.css';

const validKeys = [
  'assignedTo',
  'priority',
  'projects',
  'creator',
  'resolution',
  'dueDate',
  'tags',
];

const DATE_RANGE_KEYS = ['dueDate'];

// Sentinels that are mutually exclusive with real selections for the same key —
// the backend only honours them when they are the sole value (e.g. `assignedTo=no-assignee`).
const EXCLUSIVE_SENTINELS = {
  assignedTo: 'no-assignee',
  projects: 'no-project',
};

const truncateLength = 19;

const truncateFilterName = (str) => {
  if (str.length <= truncateLength) return str;
  return str.slice(0, truncateLength - 3) + '...';
};

const getTooltipTitle = (str) => {
  return str.length <= truncateLength ? '' : str;
};

const isFilterUnmodified = (a, b) => {
  const normalize = (obj) => ({
    assignedTo: (obj?.assignedTo || []).map(String).sort(),
    priority: (obj?.priority || []).map((p) => p._id).sort(),
    projects: (obj?.projects || []).map(String).sort(),
    creator: (obj?.creator || []).map(String).sort(),
    resolution: (obj?.resolution || []).map(String).sort(),
    dueDate: (obj?.dueDate || []).map(String).sort(),
    dueStartDate: (obj?.dueStartDate || []).map(String).sort(),
    dueEndDate: (obj?.dueEndDate || []).map(String).sort(),
    tags: (obj?.tags || []).map(String).sort(),
    customFields: obj?.customFields || {},
    status: obj?.status || 'all-tasks',
  });

  const objA = normalize(a);
  const objB = normalize(b);

  const arraysEqual = (arr1, arr2) =>
    arr1.length === arr2.length && arr1.every((val, i) => val === arr2[i]);

  return (
    arraysEqual(objA?.assignedTo, objB?.assignedTo) &&
    arraysEqual(objA?.priority, objB?.priority) &&
    arraysEqual(objA?.projects, objB?.projects) &&
    arraysEqual(objA?.creator, objB?.creator) &&
    arraysEqual(objA?.resolution, objB?.resolution) &&
    arraysEqual(objA?.dueDate, objB?.dueDate) &&
    arraysEqual(objA?.dueStartDate, objB?.dueStartDate) &&
    arraysEqual(objA?.dueEndDate, objB?.dueEndDate) &&
    arraysEqual(objA?.tags, objB?.tags) &&
    JSON.stringify(objA?.customFields) === JSON.stringify(objB?.customFields) &&
    objA?.status === objB?.status
  );
};

const isSelectionEmpty = (filters) => {
  for (const key in filters) {
    if (key !== 'customFields') {
      if (Array.isArray(filters[key]) && filters[key].length > 0) {
        return false;
      }
    } else {
      const customFields = filters.customFields;
      for (const fieldKey in customFields) {
        const value = customFields[fieldKey];
        if (
          (Array.isArray(value) && value.length > 0) ||
          (value !== null && value !== undefined && !Array.isArray(value))
        ) {
          return false;
        }
      }
    }
  }

  return true;
};

const TaskFilter = ({
  users = [],
  projects = [],
  onLabelSelection,
  usersCustomFilters,
  refetchCustomFilters,
  onEditUsersCustomFilter,
  handleSaveUserFilter,
  onDeleteUsersCustomFilter,
  setAppliedFilters,
  applyFilters,
  rightActions,
  excludeFilterKeys = [],
}) => {
  const intl = useIntl();

  const [searchParams, setSearchParams] = useSearchParams();

  const dispatch = useDispatch();

  const [isReady, setIsReady] = useState(false);

  const filterId = searchParams.get('filterId');

  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedLabelMenu, setSelectedLabelMenu] = useState(null);
  const [searchLabel, setSearchLabel] = useState('');
  const [searchLabelOption, setSearchLabelOption] = useState('');
  const [showCustomFilterMenu, setShowCustomFilterMenu] = useState(null);
  const [displayCustomFilterInput, setDisplayCustomFilterInput] =
    useState(false);
  const [filterName, setFilterName] = useState('');
  const [editingCustomFilter, setEditingCustomFilter] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState({
    assignedTo: [],
    priority: [],
    projects: [],
    creator: [],
    resolution: [],
    dueDate: [],
    dueStartDate: [],
    dueEndDate: [],
    tags: [],
    customFields: {},
  });
  const [showSaveDropdown, setShowSaveDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [dueDateCustomStart, setDueDateCustomStart] = useState('');
  const [dueDateCustomEnd, setDueDateCustomEnd] = useState('');
  const workspaceId = localStorage.getItem('workspaceId');

  const pillsContainerRef = useRef(null);
  const measureRef = useRef(null);
  const filterBtnRef = useRef(null);
  const moreBtnRef = useRef(null);
  const saveBtnRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [moreDropdownPos, setMoreDropdownPos] = useState({ top: 0, left: 0 });
  const [saveDropdownPos, setSaveDropdownPos] = useState({ top: 0, left: 0 });
  const [maxVisible, setMaxVisible] = useState(999);

  const computeMax = useCallback(() => {
    const container = pillsContainerRef.current;
    const measure = measureRef.current;
    if (!container || !measure || !usersCustomFilters.length) {
      setMaxVisible(999);
      return;
    }
    const available = container.clientWidth;
    const GAP = 6;   // gap-1.5
    const DIVIDER = 9;  // 1px + ~4px each side
    const RESET = 76 + GAP;
    const MORE = 82 + GAP;  // "+N more" button

    const pillEls = [...measure.children];
    let used = DIVIDER + RESET;
    let count = 0;

    for (let i = 0; i < pillEls.length; i++) {
      const w = pillEls[i].offsetWidth + GAP;
      const isLast = i === pillEls.length - 1;
      if (used + w + (isLast ? 0 : MORE) > available) break;
      used += w;
      count++;
    }
    setMaxVisible(Math.max(1, count));
  }, [usersCustomFilters]);

  useLayoutEffect(() => { computeMax(); }, [computeMax]);

  useEffect(() => {
    const el = pillsContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(computeMax);
    ro.observe(el);
    return () => ro.disconnect();
  }, [computeMax]);

  const workspaceTags = useSelector(
    (state) => state.workspaceReducer.workspaceTags,
  );

  const selectedCustomFilter = usersCustomFilters.find(
    (ucf) => ucf._id === filterId,
  );

  const customFields = projects?.reduce((projectAccumulator, project) => {
    const cfArray = project?.customFields || [];
    const dropdownFields = cfArray
      .filter((cf) => cf?.type === 'dropdown')
      .map((cf) => ({
        key: cf?._id,
        label: cf?.name,
        projectName: project?.title,
        icon: (
          <img
            src="/images/icons/tag-filled.svg"
            alt=""
            style={{
              height: '16px',
              width: '16px',
              marginRight: '10px',
            }}
          />
        ),
        options: cf?.possibleValues?.map((pv) => ({
          _id: pv?._id,
          label: pv?.label,
          type: 'customField',
          customFieldId: cf?._id,
        })),
      }));
    return projectAccumulator.concat(dropdownFields);
  }, []);

  const filters = [
    {
      key: 'allFilters',
      label: 'All Filters',
      options: [],
    },
    {
      key: 'assignedTo',
      label: 'Assignee',
      icon: (
        <img
          src="/images/icons/members-gray.svg"
          alt=""
          style={{
            height: '16px',
            width: '16px',
            marginRight: '10px',
          }}
        />
      ),
      options: [
        { _id: 'no-assignee', label: 'No assignee' },
        ...(users.map((user) => {
          return {
            _id: user._id,
            label: user.fullName,
            icon: (
              <UserAvatar
                alt="User"
                classNames="rounded-full w-5 h-5 shrink-0"
                fullName={user.fullName}
                userId={user?._id}
                src={user?.avi}
                styles={{ width: '20px', height: '20px', objectFit: 'cover' }}
              />
            ),
          };
        }) || []),
      ],
    },
    {
      key: 'priority',
      label: 'Priority',
      icon: (
        <img
          src="/images/icons/priority-gray.svg"
          alt=""
          style={{
            height: '16px',
            width: '16px',
            marginRight: '10px',
          }}
        />
      ),
      options: Object.keys(PRIORITY_LABEL).map((priority) => ({
        _id: priority,
        label: PRIORITY_LABEL[priority],
        color: PRIORITY_COLORS[priority],
        icon: (
          <img
            src={`/images/icons/${PRIORITY_ICON[priority]}.svg`}
            alt=""
            style={{
              height: '13px',
              width: '13px',
            }}
          />
        ),
      })),
    },
    {
      key: 'projects',
      label: 'Project',
      icon: (
        <img
          src="/images/icons/projects.svg"
          alt=""
          style={{
            height: '16px',
            width: '16px',
            filter: 'invert(40%) brightness(120%)',
            marginRight: '10px',
          }}
        />
      ),
      options: [
        { _id: 'no-project', label: 'No project' },
        ...(projects.map((project) => {
          return {
            _id: project._id,
            label: project.title,
          };
        }) || []),
      ],
    },
    {
      key: 'creator',
      label: 'Created By',
      icon: (
        <UserAddOutlined
          style={{
            fontSize: '16px',
            color: '#002337',
            opacity: '0.3',
            marginRight: '10px',
          }}
        />
      ),
      options: users.map((user) => {
        return {
          _id: user._id,
          label: user.fullName,
          icon: <UserIcon avi={user?.avi} />,
        };
      }),
    },
    {
      key: 'resolution',
      label: 'Completion',
      icon: (
        <CheckOutlined
          style={{
            fontSize: '16px',
            color: '#002337',
            opacity: '0.3',
            marginRight: '10px',
          }}
        />
      ),
      options: [
        { _id: true, label: 'Completed Tasks' },
        { _id: false, label: 'Incompleted Tasks' },
      ],
    },
    {
      key: 'dueDate',
      label: 'Due Date',
      icon: (
        <img
          src="/images/icons/calendar-gray.svg"
          alt=""
          style={{
            height: '16px',
            width: '16px',
            marginRight: '10px',
          }}
        />
      ),
      options: [
        { _id: 'today', label: 'Today' },
        { _id: 'tomorrow', label: 'Tomorrow' },
        { _id: 'overdue', label: 'Overdue' },
        { _id: 'this-week', label: 'This Week' },
        { _id: 'no-due-date', label: 'No Due Date' },
      ],
    },
    {
      key: 'dueStartDate',
      label: 'Due Start Date',
      icon: (
        <img
          src="/images/icons/calendar-gray.svg"
          alt=""
          style={{
            height: '16px',
            width: '16px',
            marginRight: '10px',
          }}
        />
      ),
      options: [],
      isHidden: true,
    },
    {
      key: 'dueEndDate',
      label: 'Due End Date',
      icon: (
        <img
          src="/images/icons/calendar-gray.svg"
          alt=""
          style={{
            height: '16px',
            width: '16px',
            marginRight: '10px',
          }}
        />
      ),
      options: [],
      isHidden: true,
    },
    {
      key: 'tags',
      label: 'Tag',
      icon: (
        <img
          src="/images/icons/tag.svg"
          alt=""
          style={{
            height: '16px',
            width: '16px',
            marginRight: '10px',
          }}
        />
      ),
      options: workspaceTags.map((tag) => {
        return {
          _id: tag._id,
          label: tag.tag,
        };
      }),
    },
    ...customFields,
  ];

  const toggleMenu = () => {
    if (menuOpen) {
      setMenuOpen(false);
      setSelectedLabelMenu(null);
      setSearchLabel('');
      setSearchLabelOption('');
    } else {
      if (filterBtnRef.current) {
        const rect = filterBtnRef.current.getBoundingClientRect();
        setDropdownPos({ top: rect.bottom + 6, left: rect.left });
      }
      setMenuOpen(true);
    }
  };

  const handleMenuClickAway = () => {
    if (!menuOpen) return;
    setMenuOpen(false);
    setSelectedLabelMenu(null);
    setSearchLabel('');
    setSearchLabelOption('');
  };

  const handleSearchLabelChange = (e) => {
    setSearchLabel(e.target.value);
  };

  const handleSearchLabelOptionChange = (e) => {
    setSearchLabelOption(e.target.value);
  };

  const handleLabelMenuClick = (filter) => {
    setSelectedLabelMenu(selectedLabelMenu?.key === filter.key ? null : filter);
    setSearchLabelOption('');
  };

  const resetFilter = () => {
    setSelectedFilters({
      assignedTo: [],
      priority: [],
      projects: [],
      creator: [],
      resolution: [],
      dueDate: [],
      dueStartDate: [],
      dueEndDate: [],
      tags: [],
      customFields: {},
    });

    setDisplayCustomFilterInput('');
    setEditingCustomFilter(null);
    setFilterName('');
    setSearchParams({});
  };

  const getFiltersToAppend = (filterKey, option) => {
    const updatedFilters = {
      ...selectedFilters,
      customFields: {
        ...selectedFilters.customFields,
      },
    };

    const isDateRangeKey = DATE_RANGE_KEYS.includes(filterKey);

    if (!validKeys.includes(filterKey)) {
      const customField = updatedFilters.customFields[filterKey];
      if (!customField) {
        updatedFilters.customFields[filterKey] = [];
      }
      const existingValue = updatedFilters.customFields[filterKey].find(
        (v) => v._id === option._id,
      );
      if (existingValue) {
        updatedFilters.customFields[filterKey] = updatedFilters.customFields[
          filterKey
        ].filter((v) => v._id !== option._id);
      } else {
        updatedFilters.customFields[filterKey] = [
          ...updatedFilters.customFields[filterKey],
          { _id: option._id },
        ];
      }
      return updatedFilters;
    }

    const existingValue = updatedFilters[filterKey].find(
      (v) => v._id === option._id,
    );

    if (!isDateRangeKey) {
      const sentinel = EXCLUSIVE_SENTINELS[filterKey];
      if (existingValue) {
        updatedFilters[filterKey] = updatedFilters[filterKey].filter(
          (v) => v._id !== option._id,
        );
      } else if (sentinel && option._id === sentinel) {
        // "No assignee" / "No project" replaces any real selections.
        updatedFilters[filterKey] = [{ _id: option._id }];
      } else {
        // Selecting a real value clears the exclusive sentinel if it was set.
        const base = sentinel
          ? updatedFilters[filterKey].filter((v) => v._id !== sentinel)
          : updatedFilters[filterKey];
        updatedFilters[filterKey] = [...base, { _id: option._id }];
      }
      return updatedFilters;
    }

    if (option === 'clear') {
      updatedFilters['dueStartDate'] = [];
      updatedFilters['dueEndDate'] = [];
      updatedFilters[filterKey] = [];
      return updatedFilters;
    }

    if (option?.length !== 2) {
      updatedFilters['dueStartDate'] = [];
      updatedFilters['dueEndDate'] = [];
      // Clicking the active preset again clears it (toggle off), instead of
      // re-applying the same value — which left the page stuck loading.
      updatedFilters[filterKey] = existingValue ? [] : [{ _id: option._id }];
      return updatedFilters;
    }

    updatedFilters['dueStartDate'] = [
      { _id: new Date(option[0] + 'T00:00:00.00').toISOString() },
    ];

    if (option[1]) {
      updatedFilters['dueEndDate'] = [
        { _id: new Date(option[1] + 'T23:59:59.999').toISOString() },
      ];
    } else {
      updatedFilters['dueEndDate'] = [];
    }
    updatedFilters['dueDate'] = [];

    return updatedFilters;
  };

  const handleFilterSelection = (filterKey, option) => {
    const updatedFilters = getFiltersToAppend(filterKey, option);

    appendFiltersToUrl(updatedFilters, filterId);
  };

  const appendFiltersToUrl = (filters, filterId, overwrite) => {
    applyFilters();
    let newSearchParams = null;
    if (overwrite) {
      newSearchParams = new URLSearchParams();
    } else {
      newSearchParams = new URLSearchParams(searchParams);
    }

    if (filterId) {
      newSearchParams.set('filterId', filterId);
    } else {
      newSearchParams.delete('filterId');
    }

    Object.entries(filters).forEach(([key, value]) => {
      if (key === 'customFields') {
        Object.entries(value).forEach(([fieldKey, fieldValues]) => {
          if (Array.isArray(fieldValues) && fieldValues.length > 0) {
            newSearchParams.set(
              fieldKey,
              fieldValues.map((v) => v._id).join(','),
            );
          } else {
            newSearchParams.delete(fieldKey);
          }
        });
      } else if (Array.isArray(value) && value.length > 0) {
        if (
          typeof value[0] === 'object' &&
          value[0] !== null &&
          value[0]._id !== undefined
        ) {
          newSearchParams.set(key, value.map((v) => v._id).join(','));
        } else {
          newSearchParams.set(key, value.join(','));
        }
      } else {
        newSearchParams.delete(key);
      }
    });

    setSearchParams(newSearchParams);
  };

  const parseFiltersFromURL = (existingFilters = {}) => {
    const updatedFilters = {
      assignedTo: [],
      priority: [],
      projects: [],
      creator: [],
      resolution: [],
      dueDate: [],
      dueStartDate: [],
      dueEndDate: [],
      tags: [],
      customFields: {},
      ...existingFilters,
    };

    const filterKeys = new Set([
      'assignedTo',
      'priority',
      'projects',
      'creator',
      'resolution',
      'dueDate',
      'dueStartDate',
      'dueEndDate',
      'tags',
    ]);

    const validUserIds = new Set(users.map((u) => u._id));
    const validProjectIds = new Set(projects.map((p) => p._id));
    const validTagIds = new Set(workspaceTags.map((t) => t._id));
    const validCustomFilterIds = new Set(usersCustomFilters.map((f) => f._id));

    const customFieldMap = new Map(
      customFields.map((cf) => [
        cf.key,
        new Set(cf.options.map((opt) => opt._id)),
      ]),
    );

    const newSearchParams = new URLSearchParams(searchParams);

    const mergeIds = (existingArr, newIds) => {
      const combined = [...existingArr, ...newIds.map((id) => ({ _id: id }))];
      return combined.filter(
        (v, i, arr) => arr.findIndex((e) => e._id === v._id) === i,
      );
    };

    const updateSearchParams = (key, ids) => {
      if (ids.length > 0) {
        newSearchParams.set(key, ids.join(','));
      } else {
        newSearchParams.delete(key);
      }
    };

    for (const [key, value] of searchParams.entries()) {
      if (key === 'selectedTask') continue;

      if (key === 'filterId') {
        if (!validCustomFilterIds.has(value)) {
          newSearchParams.delete('filterId');
        }
        continue;
      }

      if (!filterKeys.has(key) && !customFieldMap.has(key)) {
        newSearchParams.delete(key);
        continue;
      }

      if (
        (key === 'dueStartDate' || key === 'dueEndDate') &&
        isNaN(Date.parse(value))
      ) {
        newSearchParams.delete(key);
        continue;
      }

      const ids = value.split(',');

      switch (true) {
        case key === 'resolution':
          updatedFilters[key] = ids.map((id) => ({ _id: id === 'true' }));
          break;

        case key === 'assignedTo': {
          const validIds = ids.filter(
            (id) => id === 'no-assignee' || validUserIds.has(id),
          );
          updatedFilters[key] = mergeIds(updatedFilters[key] || [], validIds);
          updateSearchParams(key, validIds);
          break;
        }

        case key === 'creator': {
          const validIds = ids.filter((id) => validUserIds.has(id));
          updatedFilters[key] = mergeIds(updatedFilters[key] || [], validIds);
          updateSearchParams(key, validIds);
          break;
        }

        case key === 'projects': {
          const validIds = ids.filter(
            (id) => id === 'no-project' || validProjectIds.has(id),
          );
          updatedFilters[key] = mergeIds(updatedFilters[key] || [], validIds);
          updateSearchParams(key, validIds);
          break;
        }

        case key === 'tags': {
          const validIds = ids.filter((id) => validTagIds.has(id));
          updatedFilters[key] = mergeIds(updatedFilters[key] || [], validIds);
          updateSearchParams(key, validIds);
          break;
        }

        default:
          if (customFieldMap.has(key)) {
            const validOptions = customFieldMap.get(key);
            const validIds = ids.filter((id) => validOptions.has(id));
            if (validIds.length > 0) {
              updatedFilters.customFields[key] = mergeIds(
                updatedFilters.customFields[key] || [],
                validIds,
              );
            }
            updateSearchParams(key, validIds);
          } else {
            updatedFilters[key] = mergeIds(updatedFilters[key] || [], ids);
          }
      }
    }

    setSearchParams(newSearchParams);

    return updatedFilters;
  };

  const handleRemoveLabelSelection = (key) => {
    const newFilters = { ...selectedFilters };
    if (validKeys.includes(key)) {
      newFilters[key] = [];
    } else {
      newFilters.customFields[key] = [];
    }
    appendFiltersToUrl(newFilters);
  };

  const isValueChecked = (item) => {
    if (validKeys.includes(selectedLabelMenu.key)) {
      return selectedFilters[selectedLabelMenu.key].some(
        (v) => v._id === item._id,
      );
    }

    const key = selectedLabelMenu.key;
    const customField = selectedFilters.customFields[key];
    if (customField) {
      return selectedFilters.customFields[key].some((v) => v._id === item._id);
    }
  };

  const onSelectUsersCustomFilter = (customFilter) => {
    if (!customFilter) {
      setSearchParams({});
    } else {
      appendFiltersToUrl(customFilter.query, customFilter._id);
    }
    handleHideSaveFilterInput();
  };

  const handleDisplaySaveFilterInput = () => {
    setFilterName('');
    setDisplayCustomFilterInput(true);
  };

  const handleHideSaveFilterInput = () => {
    setDisplayCustomFilterInput(false);
    setEditingCustomFilter(null);
    setFilterName('');
  };

  const saveFilter = () => {
    setIsLoading(true);

    const filteredCustomFilters = usersCustomFilters.filter(
      (filter) => filter._id !== editingCustomFilter?._id,
    );

    const filterNameExixts = filteredCustomFilters.some(
      (customFilter) => customFilter.name === filterName,
    );

    if (filterNameExixts) {
      notification.error({
        message: 'Filter name already exists',
        placement: 'bottomRight',
      });
      setIsLoading(false);
      return;
    }

    if (editingCustomFilter) {
      onEditUsersCustomFilter(
        { ...editingCustomFilter, query: selectedFilters },
        filterName,
        (res) => {
          dispatch(
            tasksApi.util.updateQueryData(
              'getCustomFilters',
              { workspaceId },
              (draft) => {
                const index = draft.data.findIndex((f) => f._id === res._id);
                if (index !== -1) {
                  draft.data[index] = res;
                }
              },
            ),
          );
        },
      );

      setDisplayCustomFilterInput(false);
      setEditingCustomFilter(null);
      setIsLoading(false);
    } else {
      handleSaveUserFilter(selectedFilters, filterName, (res) => {
        dispatch(
          tasksApi.util.updateQueryData(
            'getCustomFilters',
            { workspaceId },
            (draft) => {
              draft.data.push(res);
            },
          ),
        );

        setIsLoading(false);
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('filterId', res._id);
        setSearchParams(newSearchParams);
      });

      setDisplayCustomFilterInput(false);
    }
  };

  const quickSaveCustomFilter = (customFilter) => {
    setIsLoading(true);
    const filterName = customFilter.name;

    onEditUsersCustomFilter(
      { ...customFilter, query: selectedFilters },
      filterName,
      () => {
        refetchCustomFilters({ workspaceId });
      },
    );
    setIsLoading(false);
  };

  const handleDeleteUsersCustomFilter = (filter) => {
    onDeleteUsersCustomFilter(filter);
    setShowCustomFilterMenu(null);
    if (editingCustomFilter?._id === filter._id) {
      setEditingCustomFilter(null);
      setDisplayCustomFilterInput(null);
    }
    if (filterId === filter._id) {
      setSearchParams({});
    }
    dispatch(
      tasksApi.util.updateQueryData(
        'getCustomFilters',
        { workspaceId },
        (draft) => {
          const index = draft.data.findIndex((f) => f._id === filter._id);
          if (index !== -1) draft.data.splice(index, 1);
        },
      ),
    );
  };

  const handleEditUsersCustomFilter = (customFilter) => {
    setShowCustomFilterMenu(null);
    setEditingCustomFilter(customFilter);
    setDisplayCustomFilterInput(true);
    setFilterName(customFilter.name);

    const overwrite = true;
    appendFiltersToUrl(customFilter.query, customFilter._id, overwrite);
  };

  const handleMoreFiltersClickAway = () => {
    setShowMoreFilters(false);
  };

  const handleUndoChanges = () => {
    let defaultFilters = {};

    if (filterId && usersCustomFilters?.length) {
      const foundCustomFilter = usersCustomFilters.find(
        (ucf) => ucf._id === filterId,
      );
      if (foundCustomFilter) {
        defaultFilters = JSON.parse(JSON.stringify(foundCustomFilter.query));
      }
    }

    const overwrite = true;

    appendFiltersToUrl(defaultFilters, filterId, overwrite);
  };

  useEffect(() => {
    if (!isReady) return;

    const handler = setTimeout(() => {
      onLabelSelection(selectedFilters);
    }, 800); // 300ms debounce

    return () => {
      clearTimeout(handler);
    };
  }, [selectedFilters, isReady]);

  useEffect(() => {
    if (isReady) return;

    const filterId = searchParams.get('filterId');
    let defaultFilters = {};

    if (filterId && usersCustomFilters?.length) {
      const foundCustomFilter = usersCustomFilters.find(
        (ucf) => ucf._id === filterId,
      );
      if (foundCustomFilter) {
        defaultFilters = JSON.parse(JSON.stringify(foundCustomFilter.query));
      }
    }

    const initialFilters = parseFiltersFromURL(defaultFilters);

    setSelectedFilters(initialFilters);
    setIsReady(true);
  }, [isReady, searchParams, usersCustomFilters]);

  useEffect(() => {
    if (!isReady) return;

    const liveFilters = parseFiltersFromURL();
    setSelectedFilters(liveFilters);
    setAppliedFilters?.(liveFilters);
  }, [searchParams, isReady]);

  const filteredLabels = filters
    .filter((item) => !item.isHidden)
    .filter((item) => !excludeFilterKeys.includes(item.key))
    .filter((item) => {
      return item.label.toLowerCase().includes(searchLabel.toLowerCase());
    });

  const filteredLabelOptions = selectedLabelMenu?.options?.filter((item) =>
    (item.label ?? '').toString().toLowerCase().includes(searchLabelOption.toLowerCase()),
  ) ?? [];

  const assignedToValues = selectedFilters?.assignedTo.map((at) =>
    filters
      .find((f) => f.key === 'assignedTo')
      .options.find((oa) => oa._id === at._id),
  );

  const priorityValues = selectedFilters?.priority.map((p) =>
    filters
      .find((f) => f.key === 'priority')
      .options.find((op) => op._id === p._id),
  );

  const projectsValues = selectedFilters?.projects.map((p) =>
    filters
      .find((f) => f.key === 'projects')
      .options.find((op) => op._id === p._id),
  );

  const creatorValues = selectedFilters?.creator.map((c) =>
    filters
      .find((f) => f.key === 'creator')
      .options.find((oc) => oc._id === c._id),
  );

  const resolutionValues = selectedFilters?.resolution.map((r) =>
    filters
      .find((f) => f.key === 'resolution')
      .options.find((or) => or._id === r._id),
  );

  const dueDateValues = selectedFilters?.dueDate.map((dd) =>
    filters
      .find((f) => f.key === 'dueDate')
      .options.find((odd) => odd._id === dd._id),
  );

  const tagsValues = selectedFilters?.tags.map((t) =>
    filters
      .find((f) => f.key === 'tags')
      .options.find((ot) => ot._id === t._id),
  );

  const selectedCustomFieldIds = Object.keys(selectedFilters.customFields);

  const selectedCustomFields = selectedCustomFieldIds
    .map((id) => {
      const foundFilter = filters.find((f) => f.key === id);
      if (foundFilter) {
        const selectedOptions = selectedFilters.customFields[id];

        return { ...foundFilter, selectedOptions };
      }
      return null;
    })
    .filter((item) => item);

  const noSelection = isSelectionEmpty(selectedFilters);

  const getFilterSelectionCount = (filterKey) => {
    if (filterKey === 'allFilters') return 0;
    if (filterKey === 'dueDate') {
      return (selectedFilters.dueDate?.length > 0 || selectedFilters.dueStartDate?.length > 0) ? 1 : 0;
    }
    if (validKeys.includes(filterKey)) {
      return (selectedFilters[filterKey] || []).length;
    }
    return (selectedFilters.customFields?.[filterKey] || []).length;
  };

  const isSaveDisabled =
    editingCustomFilter ||
    noSelection ||
    isFilterUnmodified(selectedCustomFilter?.query, selectedFilters);

  const indexSelectedCustomFilter = usersCustomFilters?.findIndex(
    (filter) => filter._id === selectedCustomFilter?._id,
  );

  return (
    <div className="w-full task-filter shadow mb-1">
      {/* Hidden measurement container — renders all pills at natural width so ResizeObserver can count how many fit */}
      <div
        ref={measureRef}
        aria-hidden="true"
        style={{
          position: 'absolute',
          visibility: 'hidden',
          pointerEvents: 'none',
          display: 'flex',
          gap: 6,
          top: -9999,
          left: 0,
          whiteSpace: 'nowrap',
        }}
      >
        {usersCustomFilters.map((filter) => (
          <div
            key={filter._id}
            className="inline-flex items-center h-7 rounded-md text-xs font-medium border px-3 flex-shrink-0"
          >
            {truncateFilterName(filter.name)}
            <span className="border-l border-gray-200 px-1.5 flex items-center h-full ml-1">
              <EditOutlined style={{ fontSize: 10 }} />
            </span>
          </div>
        ))}
      </div>

      {displayCustomFilterInput && (
        <div className="px-3 border-b border-gray-100">
          <input
            type="text"
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            placeholder={intl.formatMessage({
              id: 'FILTER_NAME_PLACEHOLDER',
              defaultMessage: 'Enter filter name...',
            })}
            className="w-full px-2 py-2 text-sm bg-transparent focus:outline-none text-gray-700 placeholder-gray-400"
          />
        </div>
      )}
      <div className="w-full flex items-center gap-2 px-4 h-11 bg-white border-b border-gray-100 sticky top-0 z-10 overflow-x-auto scrollbar-hide">
        <div className="flex items-center gap-2 flex-shrink-0">
          <ClickAwayListener onClickAway={handleMenuClickAway}>
            <div className="relative">
              <button
                ref={filterBtnRef}
                type="button"
                className="flex items-center gap-1.5 h-7 px-3 rounded-md bg-white border border-gray-200 text-xs font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-all duration-150"
                onClick={() => {
                  toggleMenu();
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/>
                </svg>
                Filters
              </button>
              {menuOpen && createPortal(
                <div
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden flex"
                  style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, boxShadow: '0 8px 30px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)', zIndex: 99999 }}
                >
                  {/* ── Left panel: filter categories ── */}
                  <div className="flex flex-col" style={{ width: 216 }}>
                    {/* Search */}
                    <div className="p-2.5">
                      <div className="relative">
                        <svg className="absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                        </svg>
                        <input
                          type="text"
                          value={searchLabel}
                          onChange={handleSearchLabelChange}
                          placeholder="Search filters…"
                          className="w-full pl-7 pr-2 h-7 text-[12px] bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 focus:bg-white text-gray-700 placeholder-gray-400 transition-colors"
                        />
                      </div>
                    </div>

                    {/* Category list */}
                    <div className="overflow-y-auto" style={{ maxHeight: 280 }}>
                      {filteredLabels.filter((f) => f.key !== 'allFilters').map((filter) => {
                        const isActive = selectedLabelMenu?.key === filter.key;
                        const count = getFilterSelectionCount(filter.key);
                        return (
                          <button
                            key={filter.key}
                            type="button"
                            className={`w-full flex items-center px-3 py-2.5 text-[13px] transition-colors ${
                              isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
                            }`}
                            onClick={() => handleLabelMenuClick(filter)}
                          >
                            {/* icon has marginRight:10px baked in — no extra gap needed */}
                            <span className="flex-shrink-0">{filter.icon}</span>
                            <span className="flex-1 text-left min-w-0">
                              <span className="block font-[450]">{filter.label}</span>
                              {filter.projectName && (
                                <span className="block text-[11px] text-gray-400 truncate leading-tight">{filter.projectName}</span>
                              )}
                            </span>
                            {count > 0 && (
                              <span className="flex-shrink-0 mr-1.5 text-[10px] font-semibold text-blue-600 bg-blue-100 rounded-full h-4 min-w-[16px] flex items-center justify-center px-1">
                                {count}
                              </span>
                            )}
                            <svg
                              width="12" height="12" viewBox="0 0 24 24" fill="none"
                              stroke={isActive ? '#2563eb' : '#d1d5db'}
                              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                              className="flex-shrink-0"
                            >
                              <polyline points="9 18 15 12 9 6"/>
                            </svg>
                          </button>
                        );
                      })}
                    </div>

                    {/* Footer */}
                    {!noSelection && (
                      <div className="px-3 py-2 border-t border-gray-100 flex items-center justify-end">
                        <button
                          type="button"
                          className="text-[11px] text-red-500 hover:text-red-600 font-medium transition-colors"
                          onClick={() => resetFilter()}
                        >
                          Clear all
                        </button>
                      </div>
                    )}
                  </div>

                  {/* ── Right panel: filter options ── */}
                  {selectedLabelMenu && (
                    <div className="flex flex-col border-l border-gray-100" style={{ width: 216 }}>
                      {selectedLabelMenu.key === 'dueDate' ? (
                        <>
                          {/* Preset options */}
                          <div className="overflow-y-auto" style={{ maxHeight: 200 }}>
                            {filteredLabelOptions.map((item, index) => {
                              const checked = isValueChecked(item);
                              return (
                                <button
                                  key={index}
                                  type="button"
                                  className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-gray-700 hover:bg-gray-50 transition-colors"
                                  onClick={() => handleFilterSelection('dueDate', item)}
                                >
                                  <div className={`flex-shrink-0 w-[14px] h-[14px] rounded-[3px] border-[1.5px] flex items-center justify-center transition-all ${checked ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'}`}>
                                    {checked && (
                                      <svg width="8" height="6" viewBox="0 0 8 6" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="1,5 3,3 7,1"/>
                                      </svg>
                                    )}
                                  </div>
                                  <span className="flex-1 text-left">{item.label}</span>
                                </button>
                              );
                            })}
                          </div>

                          {/* Custom range */}
                          <div className="px-3 py-2.5 border-t border-gray-100">
                            <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Custom range</div>
                            <div className="flex flex-col gap-1.5">
                              <input
                                type="date"
                                value={dueDateCustomStart}
                                onChange={(e) => {
                                  setDueDateCustomStart(e.target.value);
                                  handleFilterSelection('dueDate', [e.target.value, dueDateCustomEnd]);
                                }}
                                className="w-full h-7 text-[12px] bg-gray-50 border border-gray-200 rounded-lg px-2 focus:outline-none focus:border-blue-400 text-gray-700"
                              />
                              <input
                                type="date"
                                value={dueDateCustomEnd}
                                onChange={(e) => {
                                  setDueDateCustomEnd(e.target.value);
                                  handleFilterSelection('dueDate', [dueDateCustomStart, e.target.value]);
                                }}
                                className="w-full h-7 text-[12px] bg-gray-50 border border-gray-200 rounded-lg px-2 focus:outline-none focus:border-blue-400 text-gray-700"
                              />
                            </div>
                          </div>

                          {/* Footer */}
                          {getFilterSelectionCount('dueDate') > 0 && (
                            <div className="px-3 py-2 border-t border-gray-100 flex items-center justify-between">
                              <span className="text-[11px] text-gray-400">Date set</span>
                              <button
                                type="button"
                                className="text-[11px] text-gray-500 hover:text-gray-700 font-medium transition-colors"
                                onClick={() => {
                                  setDueDateCustomStart('');
                                  setDueDateCustomEnd('');
                                  handleFilterSelection('dueDate', 'clear');
                                }}
                              >
                                Clear
                              </button>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          {/* Search */}
                          <div className="p-2.5">
                            <div className="relative">
                              <svg className="absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                              </svg>
                              <input
                                type="text"
                                value={searchLabelOption}
                                onChange={handleSearchLabelOptionChange}
                                placeholder={`Search ${selectedLabelMenu.label.toLowerCase()}…`}
                                className="w-full pl-7 pr-2 h-7 text-[12px] bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 focus:bg-white text-gray-700 placeholder-gray-400 transition-colors"
                              />
                            </div>
                          </div>

                          {/* Option list */}
                          <div className="overflow-y-auto" style={{ maxHeight: 280 }}>
                            {filteredLabelOptions.length === 0 ? (
                              <div className="px-3 py-5 text-center text-[12px] text-gray-400">
                                No results
                              </div>
                            ) : (
                              filteredLabelOptions.map((item, index) => {
                                const checked = isValueChecked(item);
                                return (
                                  <button
                                    key={index}
                                    type="button"
                                    className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-gray-700 hover:bg-gray-50 transition-colors"
                                    onClick={() => handleFilterSelection(selectedLabelMenu.key, item)}
                                  >
                                    {/* Custom checkbox */}
                                    <div
                                      className={`flex-shrink-0 w-[14px] h-[14px] rounded-[3px] border-[1.5px] flex items-center justify-center transition-all ${
                                        checked ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'
                                      }`}
                                    >
                                      {checked && (
                                        <svg width="8" height="6" viewBox="0 0 8 6" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                          <polyline points="1,5 3,3 7,1"/>
                                        </svg>
                                      )}
                                    </div>
                                    {/* Icon */}
                                    {item.icon && (
                                      <div className="flex-shrink-0 w-5 h-5 rounded-full overflow-hidden flex items-center justify-center">{item.icon}</div>
                                    )}
                                    {/* Label */}
                                    <span
                                      style={{ color: item?.color || 'inherit' }}
                                      className="flex-1 text-left truncate"
                                    >
                                      {item.label}
                                    </span>
                                  </button>
                                );
                              })
                            )}
                          </div>

                          {/* Footer: selected count + clear */}
                          {getFilterSelectionCount(selectedLabelMenu.key) > 0 && (
                            <div className="px-3 py-2 border-t border-gray-100 flex items-center justify-between">
                              <span className="text-[11px] text-gray-400">
                                {getFilterSelectionCount(selectedLabelMenu.key)} selected
                              </span>
                              <button
                                type="button"
                                className="text-[11px] text-gray-500 hover:text-gray-700 font-medium transition-colors"
                                onClick={() => handleRemoveLabelSelection(selectedLabelMenu.key)}
                              >
                                Clear
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              , document.body)}
            </div>
          </ClickAwayListener>
        </div>

        {/* Saved filter pills — auto-fit middle section */}
        <div
          ref={pillsContainerRef}
          className="flex items-center gap-1.5 flex-shrink-0"
        >
          {usersCustomFilters?.length > 0 && (
            <>
              <div className="h-4 w-px bg-gray-200 flex-shrink-0" />
              <button
                type="button"
                onClick={() => resetFilter()}
                className={`inline-flex items-center h-7 px-3 rounded-md text-xs font-medium border transition-colors flex-shrink-0 ${noSelection ? 'bg-gray-100 border-gray-200 text-gray-400' : 'bg-blue-600 border-blue-600 text-white hover:bg-blue-700'}`}
              >
                Reset
              </button>
              {usersCustomFilters.slice(0, maxVisible).map((filter) => (
                <div
                  key={filter._id}
                  className={`inline-flex items-center h-7 rounded-md text-xs font-medium border transition-colors flex-shrink-0 ${
                    selectedCustomFilter?._id === filter._id
                      ? 'bg-blue-50 border-blue-200 text-blue-700'
                      : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <Tooltip placement="top" title={getTooltipTitle(filter.name)}>
                    <span
                      className="px-3 cursor-pointer"
                      onClick={() => filterId !== filter._id ? onSelectUsersCustomFilter(filter) : onSelectUsersCustomFilter(null)}
                    >
                      {truncateFilterName(filter.name)}
                    </span>
                  </Tooltip>
                  <Popover
                    content={
                      <div className="flex flex-col">
                        <div className="cursor-pointer hover:bg-gray-50 px-4 py-2 text-sm" onClick={() => handleEditUsersCustomFilter(filter)}>Edit</div>
                        <div className="cursor-pointer hover:bg-gray-50 px-4 py-2 text-sm text-red-500" onClick={() => handleDeleteUsersCustomFilter(filter)}>Delete</div>
                      </div>
                    }
                    placement="bottom"
                    trigger="click"
                    open={showCustomFilterMenu === filter?._id}
                    onOpenChange={(bool) => setShowCustomFilterMenu(bool ? filter?._id : null)}
                  >
                    <span className="border-l border-gray-200 px-1.5 cursor-pointer text-gray-400 hover:text-gray-600 flex items-center h-full">
                      <EditOutlined style={{ fontSize: 10 }} />
                    </span>
                  </Popover>
                </div>
              ))}
              {usersCustomFilters.length > maxVisible && (
                <ClickAwayListener onClickAway={handleMoreFiltersClickAway}>
                  <div className="flex-shrink-0">
                    <button
                      ref={moreBtnRef}
                      type="button"
                      className={`inline-flex items-center h-7 px-3 rounded-md text-xs font-medium border transition-colors ${
                        indexSelectedCustomFilter >= maxVisible
                          ? 'bg-blue-50 border-blue-200 text-blue-700'
                          : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                      onClick={() => {
                        if (moreBtnRef.current) {
                          const rect = moreBtnRef.current.getBoundingClientRect();
                          setMoreDropdownPos({ top: rect.bottom + 6, left: rect.left });
                        }
                        setShowMoreFilters(true);
                      }}
                    >
                      {indexSelectedCustomFilter >= maxVisible ? selectedCustomFilter?.name : `+${usersCustomFilters.length - maxVisible} more`}
                    </button>
                    {showMoreFilters && createPortal(
                      <div className="w-48 bg-white rounded-xl border border-gray-200 overflow-hidden"
                        style={{ position: 'fixed', top: moreDropdownPos.top, left: moreDropdownPos.left, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 99999 }}
                      >
                        <div className="py-1 max-h-64 overflow-y-auto">
                          {usersCustomFilters.slice(maxVisible).map((filter) => (
                            <div
                              key={filter._id}
                              className={`flex items-center justify-between px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 ${selectedCustomFilter?._id === filter._id ? 'text-blue-600 bg-blue-50' : 'text-gray-700'}`}
                              onClick={() => filterId !== filter._id ? onSelectUsersCustomFilter(filter) : onSelectUsersCustomFilter(null)}
                            >
                              <Tooltip placement="left" title={getTooltipTitle(filter.name)}>
                                <span className="truncate">{truncateFilterName(filter.name)}</span>
                              </Tooltip>
                              <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
                                <EditOutlined className="cursor-pointer text-gray-400 hover:text-gray-600" onClick={(e) => { e.stopPropagation(); handleEditUsersCustomFilter(filter); setShowMoreFilters(false); }} />
                                <DeleteOutlined className="text-red-400 cursor-pointer hover:text-red-600" onClick={(e) => { e.stopPropagation(); handleDeleteUsersCustomFilter(filter); setShowMoreFilters(false); }} />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    , document.body)}
                  </div>
                </ClickAwayListener>
              )}
            </>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {rightActions}
          {displayCustomFilterInput ? (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              className="h-7 px-3 rounded-md border border-gray-300 bg-white text-xs font-medium text-gray-600 hover:bg-gray-50 shadow-sm transition-all duration-150"
              onClick={handleHideSaveFilterInput}
            >
              <FormattedMessage id="CANCEL" defaultMessage="Cancel" />
            </button>
            <button
              type="button"
              className={`h-7 px-3 rounded-md text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 shadow-sm transition-all duration-150 ${
                isSelectionEmpty(selectedFilters) || isLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              onClick={() => saveFilter()}
              disabled={isSelectionEmpty(selectedFilters) || isLoading}
            >
              <FormattedMessage id="SAVE" defaultMessage="Save" />
            </button>
          </div>
        ) : (
          <div className="relative flex items-center gap-1">
            <button
              type="button"
              className="h-7 px-3 rounded-md text-xs font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-all duration-150"
              onClick={handleUndoChanges}
            >
              <FormattedMessage id="UNDO_CHANGES" defaultMessage="Undo" />
            </button>
            <div className="h-4 w-px bg-gray-200" />
            <button
              ref={saveBtnRef}
              type="button"
              className={`h-7 px-3 rounded-md text-xs font-semibold transition-all duration-150 ${
                isSaveDisabled
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-blue-600 hover:bg-blue-50'
              }`}
              onClick={
                isSaveDisabled
                  ? undefined
                  : () => {
                      if (selectedCustomFilter) {
                        if (saveBtnRef.current) {
                          const rect = saveBtnRef.current.getBoundingClientRect();
                          setSaveDropdownPos({ top: rect.bottom + 6, left: rect.right - 176 });
                        }
                        setShowSaveDropdown(true);
                      } else {
                        handleDisplaySaveFilterInput();
                      }
                    }
              }
              disabled={isSaveDisabled}
            >
              <FormattedMessage id="SAVE" defaultMessage="Save" />
            </button>

            {showSaveDropdown && !isSaveDisabled && selectedCustomFilter && createPortal(
              <ClickAwayListener onClickAway={() => setShowSaveDropdown(false)}>
                <div
                  className="w-44 bg-white rounded-xl border border-gray-200 shadow-xl overflow-hidden"
                  style={{ position: 'fixed', top: saveDropdownPos.top, left: saveDropdownPos.left, zIndex: 99999 }}
                >
                  <button
                    type="button"
                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    onClick={() => { setShowSaveDropdown(false); quickSaveCustomFilter(selectedCustomFilter); }}
                  >
                    <FormattedMessage id="SAVE_CHANGES" defaultMessage="Save changes" />
                  </button>
                  <div className="h-px bg-gray-100 mx-3" />
                  <button
                    type="button"
                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    onClick={() => { setShowSaveDropdown(false); handleDisplaySaveFilterInput(); }}
                  >
                    <FormattedMessage id="SAVE_AS_NEW" defaultMessage="Save as new" />
                  </button>
                </div>
              </ClickAwayListener>
            , document.body)}
          </div>
        )}
        </div>
      </div>

      {/* Applied filter pills row — only shown when filters are active */}
      {!noSelection && (
        <div className="applied-filter-pills flex flex-nowrap items-center gap-1.5 px-4 py-2 border-t border-gray-100 bg-white overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
          <AssigneePill
            values={assignedToValues}
            onRemove={() => handleRemoveLabelSelection('assignedTo')}
            options={filters.find((f) => f.key === 'assignedTo')?.options || []}
            onValueSelection={(item) => handleFilterSelection('assignedTo', item)}
          />
          <PriorityPill
            values={priorityValues}
            onRemove={() => handleRemoveLabelSelection('priority')}
            options={filters.find((f) => f.key === 'priority')?.options || []}
            onValueSelection={(item) => handleFilterSelection('priority', item)}
          />
          <ProjectPill
            values={projectsValues}
            onRemove={() => handleRemoveLabelSelection('projects')}
            options={filters.find((f) => f.key === 'projects')?.options || []}
            onValueSelection={(item) => handleFilterSelection('projects', item)}
          />
          <CreatorPill
            values={creatorValues}
            onRemove={() => handleRemoveLabelSelection('creator')}
            options={filters.find((f) => f.key === 'creator')?.options || []}
            onValueSelection={(item) => handleFilterSelection('creator', item)}
          />
          <ResolutionPill
            values={resolutionValues}
            onRemove={() => handleRemoveLabelSelection('resolution')}
            options={filters.find((f) => f.key === 'resolution')?.options || []}
            onValueSelection={(item) => handleFilterSelection('resolution', item)}
          />
          <DueDatePill
            dueDate={dueDateValues}
            dueDateOptions={filters.find((f) => f.key === 'dueDate')?.options || []}
            dueEndDate={selectedFilters.dueEndDate}
            dueStartDate={selectedFilters.dueStartDate}
            onValueSelection={(value) => handleFilterSelection('dueDate', value)}
            onRemove={() => handleFilterSelection('dueDate', 'clear')}
          />
          <TagPill
            values={tagsValues}
            onRemove={() => handleRemoveLabelSelection('tags')}
            options={filters.find((f) => f.key === 'tags')?.options || []}
            onValueSelection={(item) => handleFilterSelection('tags', item)}
          />
          {selectedCustomFields.map((selectedCustomField) => (
            <CustomFieldPill
              key={selectedCustomField.key}
              customField={selectedCustomField}
              onRemove={() => handleRemoveLabelSelection(selectedCustomField.key)}
              onValueSelection={(item) => handleFilterSelection(selectedCustomField.key, item)}
            />
          ))}
        </div>
      )}
    </div>
  );

};

export default TaskFilter;
